import { useOperatorChatPins } from './useOperatorChatPins';
import { ChatDialogRealtimeContext } from '@/features/chatDialogRealtime/model/useChatDialogRealtime';
import { AppChatRealtimeContext } from '@/features/appChatRealtime/model/useAppChatRealtime';
import {
    useContext,
    useEffect,
    useState,
    useRef,
} from 'react';
import type { ChatDialogResponse } from '@/core/generated/api';
import { chatReadStateService } from '@/entities/ChatDialog/api/chatReadStateService';
import { getErrorMessage } from '@/shared/lib/errors';
import { useAppChatStore } from '@/entities/AppChat/model/store';
import { useChatDialogStore } from '@/entities/ChatDialog/model/store';
import type { ChatDialogSearchFilter, ChatDialogFilter } from '@/entities/ChatDialog/model/types';
import { RequestStatus } from '@/shared/model/requestStatus';
import { selectChatInboxPage } from './selectors';

/** Управляет общим списком доступных каналов переписки */
export const useChatInbox = (canViewNative: boolean, external: boolean) => {
    const realtime = useContext(AppChatRealtimeContext);
    const externalRealtime = useContext(ChatDialogRealtimeContext);
    const nativeAvailable = canViewNative && realtime?.available !== false;
    const native = nativeAvailable && realtime?.connection !== 'connecting';
    const app = useAppChatStore();
    const salebot = useChatDialogStore();
    const [limit, setLimit] = useState(30);
    const [readPending, setReadPending] = useState(false);
    const [readError, setReadError] = useState<string | null>(null);
    const readBusy = useRef(false);

    const sources = [native
        ? app
        : null, external
        ? salebot
        : null]
        .filter((source) => source !== null);

    const pinsRevision = (native
        ? realtime?.pinsRevision ?? 0
        : 0)
        + (external
            ? externalRealtime?.pinsRevision ?? 0
            : 0);

    const pins = useOperatorChatPins(native, external, sources[0] ?? app, pinsRevision);

    useEffect(() => {
        if (native && app.countersStatus === RequestStatus.Idle) void app.fetchCounters();
        if (external && salebot.countersStatus === RequestStatus.Idle) void salebot.fetchCounters();
        if (native && app.listStatus === RequestStatus.Idle) void app.fetchDialogs();
        if (external && salebot.listStatus === RequestStatus.Idle) void salebot.fetchDialogs();

        const loadApp = native && app.nextOffset !== null && app.dialogs.length < limit
            && app.listStatus === RequestStatus.Success && app.listMoreStatus !== RequestStatus.Loading && app.listMoreStatus !== RequestStatus.Error;

        if (loadApp) {
            void app.fetchMoreDialogs();
        }

        const loadExternal = external && salebot.nextOffset !== null && salebot.dialogs.length < limit
            && salebot.listStatus === RequestStatus.Success && salebot.listMoreStatus !== RequestStatus.Loading && salebot.listMoreStatus !== RequestStatus.Error;

        if (loadExternal) {
            void salebot.fetchMoreDialogs();
        }
    }, [native, external, app, salebot, limit]);

    /** Применяет условия поиска ко всем доступным каналам */
    const apply = (filter: ChatDialogSearchFilter): void => {
        setLimit(30);

        if (native) app.setSearchFilter(filter);
        if (external) salebot.setSearchFilter(filter);
    };

    /** Выбирает вкладку всех или непрочитанных переписок */
    const setFilter = (filter: ChatDialogFilter): void => {
        setLimit(30);

        if (native) app.setFilter(filter);
        if (external) salebot.setFilter(filter);
    };

    /** Повторяет загрузку доступных списков */
    const reload = async (): Promise<void> => {
        setReadError(null);
        await Promise.all([pins.reload(), ...sources.map((source) => source.refreshLive())]);
    };

    /** Загружает следующую часть общего списка */
    const more = async (): Promise<void> => {
        setLimit((previous) => previous + 30);

        await Promise.all(sources.filter((source) => source.nextOffset !== null)
            .map((source) => source.fetchMoreDialogs()));
    };

    /** Меняет чтение без открытия и обновляет счётчики и закрепления */
    const setReadState = async (dialog: ChatDialogResponse, unread: boolean): Promise<void> => {
        if (readBusy.current) return;
        readBusy.current = true;
        setReadPending(true);
        setReadError(null);

        try {
            await chatReadStateService.setReadState(dialog, unread);
            await reload();
        } catch (error: unknown) {
            setReadError(getErrorMessage(error, 'Не удалось изменить состояние чтения'));
        } finally {
            readBusy.current = false;
            setReadPending(false);
        }
    };

    const page = selectChatInboxPage(sources, limit, pins.pins);
    const error = readError ?? pins.error ?? sources.find((source) => source.listStatus === RequestStatus.Error)?.listError;

    const loading = sources.some((source) => source.listStatus === RequestStatus.Idle
        || source.listStatus === RequestStatus.Loading);

    return {
        apply,
        nativeAvailable,
        togglePin: pins.ready
            ? pins.toggle
            : undefined,
        pinPending: pins.pending,
        setReadState,
        readPending,
        list: {
            dialogs: page.dialogs,
            totalCount: sources.length > 0 && sources.every((source) => source.totalCount != null)
                ? sources.reduce((total, source) => total + (source.totalCount ?? 0), 0)
                : null,
            nextOffset: page.hasMore
                ? limit
                : null,
            filter: sources[0]?.filter ?? 'all',
            setFilter,
            listStatus: error
                ? RequestStatus.Error
                : loading
                    ? RequestStatus.Loading
                    : RequestStatus.Success,
            listError: error ?? null,
            listMoreStatus: sources.some((source) => source.listMoreStatus === RequestStatus.Loading)
                ? RequestStatus.Loading
                : RequestStatus.Idle,
            listMoreError: sources.find((source) => source.listMoreError)?.listMoreError ?? null,
            fetchDialogs: reload,
            fetchMoreDialogs: more,
        },
    };
};
